import { signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Model, ModelSelectionSource, ModelVendor, ModelType } from '@vendasta/protos/ai_assistants/v1alpha1';

export const DEFAULT_MODEL: Model = {
  id: '',
  vendor: ModelVendor.VENDOR_UNSET,
  type: ModelType.TYPE_UNSET,
  name: '',
  recommended: false,
  supportedReasoningEfforts: [],
};

export type AiModelConfigFormControls = {
  model: FormControl<Model | null>;
};

export class AiModelConfigurationForm extends FormGroup<AiModelConfigFormControls> {
  private readonly systemDefault = signal<Model | null>(null);
  private recommended: Model | null = null;
  private committedSource: ModelSelectionSource | undefined;

  /**
   * The model the platform chose for an assistant that never chose one.
   *
   * Held apart from the control so the picker can name it while the selection still reads as the default, and so
   * a save echoes it back instead of clearing it. Clearing would drop the assistant to the runtime
   * empty-to-default resolution, undoing a migration that stamped it with something else.
   */
  readonly systemDefaultModel = this.systemDefault.asReadonly();

  constructor(model?: Model, selectionSource?: ModelSelectionSource) {
    super({ model: new FormControl(DEFAULT_MODEL) });
    this.applySelection(model ?? null, selectionSource);
    this.committedSource = selectionSource;
  }

  /**
   * Names the catalog's recommended model — what an un-pin returns the assistant to when no stamp says otherwise.
   *
   * With it set, a deliberate un-pin sends this model with a SYSTEM_DEFAULT claim, the checkable form the service
   * honours, instead of an empty list it has to interpret. That keeps a restored assistant carrying an explicit
   * model rather than resolving through the runtime fallback on every answer. Only a touched control sends it: a
   * pristine model-less assistant stays model-less, or an unrelated save would materialize a model nobody chose
   * and the service would read the difference as a pin.
   */
  setRecommendedModel(model: Model | null): void {
    this.recommended = model;
  }

  /** Whether the selection reads as the default rather than a concrete pick. */
  isOnDefault(): boolean {
    const model = this.controls.model.getRawValue();
    return model == null || (!model.id && (!model.vendor || model.vendor === ModelVendor.VENDOR_UNSET));
  }

  applySelection(model: Model | null, selectionSource?: ModelSelectionSource): void {
    const isSystemDefault = selectionSource === ModelSelectionSource.SELECTION_SOURCE_SYSTEM_DEFAULT;
    this.systemDefault.set(isSystemDefault ? model : null);
    this.controls.model.setValue(isSystemDefault ? DEFAULT_MODEL : (model ?? DEFAULT_MODEL));
  }

  /**
   * Re-reads what the platform's choice is now that a save has landed.
   *
   * The form is built once and kept for the session, so what a save persisted is what later saves must echo. A
   * model of the user's own ends the platform's choice; a save made on the default persisted what
   * `toAssistantModel` sent — the echoed stamp, or the recommended model an un-pin stated — and that is the
   * stamp from here on. Getting this wrong in either direction re-sends state that stopped being current, and
   * the service reads the difference as a choice nobody made.
   */
  commitSaved(): void {
    const saved = this.toAssistantModel();
    this.systemDefault.set(this.isOnDefault() ? saved : null);
    if (saved) {
      this.committedSource = this.isOnDefault()
        ? ModelSelectionSource.SELECTION_SOURCE_SYSTEM_DEFAULT
        : ModelSelectionSource.SELECTION_SOURCE_USER_SELECTED;
    }
    // A save that sent no model made no provenance statement — the service classified it against what was
    // stored — so the committed value stands.
  }

  /**
   * The provenance this save carries: a statement when the control was touched, otherwise the last committed
   * value echoed through.
   *
   * The service infers provenance by comparing models, which goes blind when the user deliberately picks the
   * model already stored as the platform's choice: the ids match, the pick reads as an echo, and the assistant
   * stays eligible for platform-initiated moves its user just opted out of. The pick is the one fact the form
   * holds and the service cannot see, so it is stated exactly when the control was touched.
   *
   * The echo comes from what this form's saves committed, never from the assistant as the page loaded it — the
   * form outlives its loading snapshot, and echoing load-time provenance after a save moved it re-states a
   * choice nobody just made, which the service believes.
   */
  toModelSelectionSource(): ModelSelectionSource | undefined {
    if (this.controls.model.dirty && !this.controls.model.disabled) {
      if (!this.isOnDefault()) {
        return ModelSelectionSource.SELECTION_SOURCE_USER_SELECTED;
      }
      if (this.systemDefault() || this.recommended) {
        return ModelSelectionSource.SELECTION_SOURCE_SYSTEM_DEFAULT;
      }
    }
    return this.committedSource;
  }

  toAssistantModel(): Model | null {
    const model = this.controls.model.getRawValue();
    const selected = this.isOnDefault()
      ? (this.systemDefault() ?? (this.controls.model.dirty ? this.recommended : null))
      : model;
    if (selected == null) {
      return null;
    }
    return {
      id: selected.id,
      vendor: selected.vendor,
      type: selected.type,
      name: selected.name,
      recommended: selected.recommended,
      supportedReasoningEfforts: selected.supportedReasoningEfforts ?? [],
    };
  }
}
